import React, {useCallback, useRef, useState} from 'react';
import Menu from '@material-ui/core/Menu';
import theme from '../../theme';
import UserAvatar from '../Avatar';
import Divider from '@material-ui/core/Divider';
import WarningIcon from '@material-ui/icons/Warning';
import Tooltip from '@material-ui/core/Tooltip';
import MenuItemAnchor from './MenuItemAnchor';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import useSyncPromise from '../../utils/react/useSyncPromise';
import fetchUser from '../../utils/gaia/fetchUser';
import gettext from '../../utils/django/gettext';

function useIsVisible() {
    const [user,, isLoaded] = useSyncPromise(fetchUser());
    return isLoaded ? user.isAuthenticated : false;
}

function Toolbar({
    style,
    children,
    ...props
}: React.HTMLProps<HTMLAnchorElement>) {

    const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
    const ref = useRef<HTMLAnchorElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [user] = useSyncPromise(fetchUser());

    const onMenuClose = useCallback(() => setIsOpen(false), []);

    const onAnchorClick = useCallback(event => {
        event.preventDefault();
        setIsOpen(true);
    }, []);

    if (!useIsVisible() || !user) return null;

    return (<>
        <a
            {...props}
            ref={ref}
            href="#"
            onClick={onAnchorClick}
            style={{
                ...style,
                paddingRight: 0,
                paddingLeft: theme.spacing(1)
            }}
        >
            {children || <>
                <UserAvatar
                    id={user.id}
                    image={user.image}
                    name={user.displayName}
                    ranger={user.isRanger}
                    staff={user.isStaff}
                />

                {user.isSU && <Tooltip title={`Warning! Logged in as "${user.displayName}".`}>
                    <WarningIcon style={{marginRight: theme.spacing(0.5)}} color='secondary' />
                </Tooltip>}

                {isNarrow ?
                    null :
                    <span style={{paddingLeft: 6}}>{user.displayName}</span>
                }

                <ExpandMoreIcon style={{fontSize: 18}} />
            </> }
        </a>
        <Menu
            anchorEl={ref.current}
            open={isOpen}
            onClose={onMenuClose}
        >
            <HamburgerMenu />
        </Menu>
    </>);
}

// eslint-disable-next-line prefer-arrow-callback
const HamburgerMenu = React.forwardRef<HTMLDivElement>(function HamburgerMenuInner(props, ref) {
    const [user] = useSyncPromise(fetchUser());
    if (!useIsVisible() || !user) return null;

    return (<div ref={ref}>
        <MenuItemAnchor href="/datasummary/tracks/">{gettext('Tracks')}</MenuItemAnchor>
        <MenuItemAnchor href="/datasummary/routes/">{gettext('Routes')}</MenuItemAnchor>
        { user.hasSubscription ?
            <MenuItemAnchor href="/datasummary/areas/">{gettext('Areas')}</MenuItemAnchor> :
            null }
        <MenuItemAnchor href="/datasummary/waypoints/">{gettext('Waypoints')}</MenuItemAnchor>
        <MenuItemAnchor href="/datasummary/photos/">{gettext('Photos')}</MenuItemAnchor>
        <MenuItemAnchor href="/datasummary/saved/">{gettext('Hikes')}</MenuItemAnchor>
        <MenuItemAnchor href="/datasummary/maps/">{gettext('Maps')}</MenuItemAnchor>
        <MenuItemAnchor href="/datasummary/mapsources/">{gettext('Map Sources')}</MenuItemAnchor>
        <MenuItemAnchor href="/datasummary/folders/">{gettext('Folders')}</MenuItemAnchor>

        <Divider />
        <MenuItemAnchor href="/upload/">{gettext('Upload')}</MenuItemAnchor>
        <Divider />
        { user.isTeamAdmin ?
            <MenuItemAnchor href="/teams/">My Teams</MenuItemAnchor> :
            null }
        <MenuItemAnchor href="/team/">Create a Team</MenuItemAnchor>

        <Divider />
        <MenuItemAnchor href="/profile/?utm_source=header&utm_campaign=profile">
            {gettext('My Profile')}
        </MenuItemAnchor>
        <MenuItemAnchor href="/user/subscriptions/">{gettext('My Subscriptions')}</MenuItemAnchor>
        <MenuItemAnchor href="/settings/">{gettext('Settings')}</MenuItemAnchor>
        <MenuItemAnchor href="/logout/">{gettext('Log out')}</MenuItemAnchor>

        { user.isStaff ? <React.Fragment>
            <Divider />
            <MenuItemAnchor href="/report/paid_subs/">Marketing Stats</MenuItemAnchor>
            <MenuItemAnchor href="/dashboard/">GaiaCloud Stats</MenuItemAnchor>
            <MenuItemAnchor href="/admin/">Datastore</MenuItemAnchor>
            <MenuItemAnchor href="/giftcodes/">Generate Gift Code</MenuItemAnchor>
            <MenuItemAnchor href="/giftcodes/use/">Apply Gift Code</MenuItemAnchor>
        </React.Fragment> : null }
    </div>);
});

export default {Toolbar, HamburgerMenu};
